import React from 'react';
import type { AccountType, VelaSuggestion } from '../types';
import { COA_TABLE_COLUMNS } from '../constants';
import NewAccountDrawer from './NewAccountDrawer';
import VelaSuggestionsDrawer from './VelaSuggestionsDrawer';
import BulkSelectBanner from './BulkSelectBanner';
import VelaCleanupModal from './VelaCleanupModal';
import ImportChartOfAccountsModal from './ImportChartOfAccountsModal';
import ExportFormatPicker from '../../../../components/common/ExportFormatPicker';
import TableCustomizeDrawer from '../../../../components/common/TableCustomizeDrawer';
import type { useTableCustomization } from '../../../../hooks/useTableCustomization';
import type { ValidatedRow } from '../../../../hooks/useImportChartOfAccounts';

type TableCustomizationHookReturn = ReturnType<typeof useTableCustomization>;
type CustomizationDraft = TableCustomizationHookReturn['draft'];
type UpdateDraftFn = TableCustomizationHookReturn['updateDraft'];

interface Props {
  drawerOpen: boolean;
  closeDrawer: () => void;
  onCreateAccount: (draft: { number: string; name: string; type: AccountType }) => void;
  newAccountPrefill: { name?: string; type?: AccountType };
  suggestionsOpen: boolean;
  closeSuggestions: () => void;
  suggestions: VelaSuggestion[];
  onSuggestionPrimary: (s: VelaSuggestion) => void;
  onSuggestionSkip: (s: VelaSuggestion) => void;
  exportOpen: boolean;
  closeExport: () => void;
  exportOptions: unknown;
  customizeOpen: boolean;
  closeCustomize: () => void;
  customizeDraft: CustomizationDraft;
  updateCustomizeDraft: UpdateDraftFn;
  applyCustomize: () => void;
  cancelCustomize: () => void;
  resetCustomize: () => void;
  selectedCount: number;
  onBulkMakeInactive: () => void;
  onBulkLockAccounts: () => void;
  onBulkDismiss: () => void;
  velaCleanupOpen: boolean;
  closeVelaCleanup: () => void;
  importOpen: boolean;
  closeImport: () => void;
  existingAccountNames: string[];
  onImport: (rows: ValidatedRow[]) => void;
}

export default function CoaModals(props: Props): React.ReactElement {
  return (
    <>
      <NewAccountDrawer
        open={props.drawerOpen}
        onClose={props.closeDrawer}
        onCreate={props.onCreateAccount}
        suggestedName={props.newAccountPrefill.name}
        suggestedType={props.newAccountPrefill.type}
      />

      <VelaSuggestionsDrawer
        open={props.suggestionsOpen}
        onClose={props.closeSuggestions}
        suggestions={props.suggestions}
        onPrimary={props.onSuggestionPrimary}
        onSkip={props.onSuggestionSkip}
      />

      <ExportFormatPicker
        open={props.exportOpen}
        onClose={props.closeExport}
        reportName="Chart of accounts"
        options={props.exportOptions as never}
      />

      <TableCustomizeDrawer
        open={props.customizeOpen}
        onClose={props.closeCustomize}
        title="Customize"
        columnDefs={COA_TABLE_COLUMNS}
        draft={props.customizeDraft}
        updateDraft={props.updateCustomizeDraft}
        onApply={props.applyCustomize}
        onResetToDefault={props.resetCustomize}
        onCancel={props.cancelCustomize}
      />

      <BulkSelectBanner
        count={props.selectedCount}
        onMakeInactive={props.onBulkMakeInactive}
        onLockAccounts={props.onBulkLockAccounts}
        onDismiss={props.onBulkDismiss}
      />

      <VelaCleanupModal
        open={props.velaCleanupOpen}
        onClose={props.closeVelaCleanup}
      />

      <ImportChartOfAccountsModal
        open={props.importOpen}
        onClose={props.closeImport}
        existingAccountNames={props.existingAccountNames}
        onImport={props.onImport}
      />
    </>
  );
}